import { Grid } from '@mui/material';
import { Checkbox, HSpacer, RoutalDialog } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Subscription } from '../../../../../domain/backoffice/subscription/Subscription';
import { useUpdateSubscriptionContract } from '../../../../../hooks/backoffice/subscriptions/useUpdateSubscriptionContract';
import Button from '../../../../../react-components/Button';
import InputText from '../../../../../react-components/InputText';
import Text from '../../../../../react-components/Text';

type BackofficeClientsOrganizationsManageContractDialogProps = {
  openDialog: boolean;
  organizationId: string;
  subscription: Subscription;
  onClose: (hasBeenUpdated: boolean) => void;
};

export const BackofficeClientsOrganizationsManageContractDialog = ({
  openDialog,
  organizationId,
  subscription,
  onClose,
}: BackofficeClientsOrganizationsManageContractDialogProps) => {
  const { run: manageSubscriptionContract, isLoading, success } = useUpdateSubscriptionContract();

  const [hasContract, setHasContract] = useState<boolean>(subscription.has_contract);
  const [vehicles, setVehicles] = useState<number>(subscription.vehicles);
  const [users, setUsers] = useState<number>(subscription.users);
  const [projects, setProjects] = useState<number>(subscription.projects);
  const [supervisors, setSupervisors] = useState<number>(subscription.supervisors);

  useEffect(() => {
    if (success) {
      onClose(true);
    }
  }, [onClose, success]);

  if (!openDialog) return null;

  const isDirtyState = () => {
    return (
      hasContract !== subscription.has_contract ||
      vehicles !== subscription.vehicles ||
      users !== subscription.users ||
      projects !== subscription.projects ||
      supervisors !== subscription.supervisors
    );
  };

  const isSaveButtonDisabled = () => {
    return isLoading || !isDirtyState();
  };

  return (
    <RoutalDialog
      open={openDialog}
      onCloseDialog={() => onClose(false)}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      titleContent={<Text variant="h6" intlId="administration.users.manage_contract.title" />}
      footerContent={
        <Grid container justifyContent="flex-end">
          <Button intlId="cancel" variant="flat" onClick={() => onClose(false)} />
          <HSpacer small />
          <Button
            intlId="confirm"
            disabled={isSaveButtonDisabled()}
            onClick={async () => {
              manageSubscriptionContract({
                organizationId,
                data: {
                  has_contract: hasContract,
                  vehicles,
                  users,
                  projects,
                  supervisors,
                },
              });
            }}
          />
        </Grid>
      }
    >
      <Grid
        style={{
          width: `100%`,
        }}
      >
        <Text variant="label" intlId="administration.users.manage_contract.has_contract" />
        <Checkbox
          checked={hasContract}
          onChange={() => {
            setHasContract(!hasContract);
          }}
        />
      </Grid>
      {hasContract && (
        <Grid
          style={{
            display: `flex`,
            flexDirection: `column`,
            width: `100%`,
            marginTop: `20px`,
          }}
        >
          <InputText
            type="number"
            label={<Text variant="label" intlId="administration.users.manage_contract.vehicles" />}
            placeholderIntlId="administration.users.manage_contract.vehicles_ph"
            fieldValue={vehicles}
            onChange={setVehicles}
            inputContainerStyles={{
              marginBottom: `20px`,
            }}
          />
          <InputText
            type="number"
            label={<Text variant="label" intlId="administration.users.manage_contract.projects" />}
            placeholderIntlId="administration.users.manage_contract.projects_ph"
            fieldValue={projects}
            onChange={setProjects}
            inputContainerStyles={{
              marginBottom: `20px`,
            }}
          />
          <InputText
            type="number"
            label={<Text variant="label" intlId="administration.users.manage_contract.supervisors" />}
            placeholderIntlId="administration.users.manage_contract.supervisors_ph"
            fieldValue={supervisors}
            onChange={setSupervisors}
            inputContainerStyles={{
              marginBottom: `20px`,
            }}
          />
          <InputText
            type="number"
            label={<Text variant="label" intlId="administration.users.manage_contract.users" />}
            placeholderIntlId="administration.users.manage_contract.users_ph"
            fieldValue={users}
            onChange={setUsers}
            inputContainerStyles={{
              marginBottom: `20px`,
            }}
          />
        </Grid>
      )}
    </RoutalDialog>
  );
};
